import { useCallback, MouseEvent, useEffect, useState, useRef } from 'react';

import { useLoginStore, LoginState, DropdownOpenDirection } from '../Login';
import picsartAuth from '../../../services/picsartAuth';
import { log } from '../Login/utils';

import { useProfileStore } from './profileStore';

const MENU_MARGIN = 12;

export function useMenuVisibility() {
  const [menuVisible, setMenuVisible] = useState(false);
  const showMenu = useCallback(() => {
    setMenuVisible(true);
  }, []);
  const toggleMenu = useCallback(() => {
    setMenuVisible(!menuVisible);
  }, [menuVisible, setMenuVisible]);
  const hideMenu = useCallback(() => setMenuVisible(false), []);

  return { showMenu, hideMenu, toggleMenu, menuVisible };
}

export function useLogout() {
  const setLoginState = useLoginStore((state) => state.setLoginState);
  const resetProfile = useProfileStore((state) => state.reset);
  const logout = useCallback(async (e: MouseEvent) => {
    e.preventDefault();

    setLoginState(LoginState.Pending);
    try {
      picsartAuth.logout();
      setLoginState(LoginState.Out);
      resetProfile();
    } catch (err) {
      setLoginState(LoginState.In);
      log(`Could not logout: ${err?.response?.data?.error}`);
    }
  }, []);

  return logout;
}

export function useGetOwnProfile() {
  const isLoggedIn = useLoginStore((state) => state.loginState === LoginState.In);
  const setProfile = useProfileStore((state) => state.set);
  const userId = useProfileStore((state) => state.id);

  useEffect(() => {
    const canGetUserProfile = isLoggedIn && !userId;

    if (canGetUserProfile) {
      (async () => {
        try {
          const profile = await picsartAuth.getOwnUserProfile();

          setProfile(profile);
        } catch (err) {
          log(`Error fetching profile: ${err?.response?.data?.error}`);
        }
      })();
    }
  }, [isLoggedIn]);
}

export function useMenuReposition(menuVisible: boolean, direction: DropdownOpenDirection) {
  const menu = useRef<HTMLUListElement>(null);
  const [top, setTop] = useState(54);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (!menuVisible || !menu?.current) {
      setLeft(0);
      return;
    }

    const { width, x } = menu.current.getBoundingClientRect();
    const offset = document.body.clientWidth - (x + width);

    if (offset < 0) {
      setLeft(offset - MENU_MARGIN);
    }

    if (direction === DropdownOpenDirection.UP) {
      setTop(-menu.current.clientHeight);
    }
  }, [menuVisible]);

  return { menu, left, top };
}
